import api from './api';

export interface ProfileDto {
  id: number;
  email: string;
  username: string;
  nombre: string;
  apellido?: string;
  telefono?: string;
  avatar?: string;
  fechaNacimiento?: string;
  genero?: string;
  universidad?: string;
  anioGraduacion?: number;
  numeroTitulo?: string;
  especialidadInteres?: string;
  bio?: string;
  pais?: string;
  tz: string;
  recibirNotificaciones: boolean;
  recibirNewsletters: boolean;
  perfilPublico: boolean;
  fechaRegistro?: string;
  ultimaActividad?: string;
  estado: string;
  emailVerificado: boolean;
  roles?: string[];
  privacy: {
    mostrarEmail: boolean;
    mostrarTelefono: boolean;
    mostrarUniversidad: boolean;
    permitirMensajes: boolean;
    mostrarEstadisticas: boolean;
  };
}

export interface UpdateProfileDto {
  nombre?: string;
  apellido?: string;
  telefono?: string;
  fechaNacimiento?: string;
  genero?: string;
  universidad?: string;
  anioGraduacion?: number;
  numeroTitulo?: string;
  especialidadInteres?: string;
  bio?: string;
  pais?: string;
  tz?: string;
  recibirNotificaciones?: boolean;
  recibirNewsletters?: boolean;
  perfilPublico?: boolean;
  privacy?: {
    mostrarEmail?: boolean;
    mostrarTelefono?: boolean;
    mostrarUniversidad?: boolean;
    permitirMensajes?: boolean;
    mostrarEstadisticas?: boolean;
  };
}

export interface ActualizarEmailRequest {
  nuevoEmail: string;
  contrasenaActual: string;
}

export interface ActualizarContrasenaRequest {
  contrasenaActual: string;
  nuevaContrasena: string;
  confirmarNuevaContrasena: string;
}

export interface ConfiguracionPrivacidadRequest {
  recibirNotificaciones?: boolean;
  recibirNewsletters?: boolean;
  perfilPublico?: boolean;
  mostrarEmail?: boolean;
  mostrarTelefono?: boolean;
  mostrarUniversidad?: boolean;
  permitirMensajes?: boolean;
  mostrarEstadisticas?: boolean;
}

class PerfilUsuarioService {
  private baseUrl = '/api/perfil';

  /**
   * Obtiene el perfil completo del usuario actual
   */
  async getMiPerfil(): Promise<ProfileDto> {
    const response = await api.get<ProfileDto>(`${this.baseUrl}/me`);
    return response.data;
  }

  /**
   * Actualiza el perfil del usuario
   */
  async actualizarPerfil(data: UpdateProfileDto): Promise<ProfileDto> {
    const response = await api.put<ProfileDto>(`${this.baseUrl}/me`, data);
    return response.data;
  }

  /**
   * Sube un nuevo avatar
   */
  async subirAvatar(file: File): Promise<ProfileDto> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ProfileDto>(`${this.baseUrl}/me/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Elimina el avatar actual
   */
  async eliminarAvatar(): Promise<ProfileDto> {
    const response = await api.delete<ProfileDto>(`${this.baseUrl}/me/avatar`);
    return response.data;
  }

  /**
   * Cambia la contraseña del usuario actual
   */
  async cambiarMiContrasena(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/api/usuarios/me/password', {
      currentPassword,
      newPassword,
    });
  }

  // ============================================================
  // UTILIDADES
  // ============================================================

  /**
   * Obtiene los países disponibles para el selector
   */
  async getPaisesDisponibles(): Promise<string[]> {
    // Por ahora devolvemos una lista estática, pero podrías crear un endpoint
    return [
      'México', 'Estados Unidos', 'Colombia', 'Argentina', 'Chile', 'Perú', 
      'Ecuador', 'Venezuela', 'Costa Rica', 'Panamá', 'Guatemala', 'El Salvador',
      'Honduras', 'Nicaragua', 'República Dominicana', 'Puerto Rico', 'Cuba',
      'España', 'Brasil', 'Uruguay', 'Paraguay', 'Bolivia'
    ];
  }

  /**
   * Obtiene las zonas horarias disponibles
   */
  async getTimezonesDisponibles(): Promise<{ [key: string]: string }> {
    return {
      'America/Mexico_City': 'Ciudad de México (GMT-6)',
      'America/Monterrey': 'Monterrey (GMT-6)',
      'America/Cancun': 'Cancún (GMT-5)',
      'America/New_York': 'Nueva York (GMT-5)',
      'America/Los_Angeles': 'Los Ángeles (GMT-8)',
      'America/Chicago': 'Chicago (GMT-6)',
      'America/Bogota': 'Bogotá (GMT-5)',
      'America/Lima': 'Lima (GMT-5)',
      'America/Santiago': 'Santiago (GMT-3)',
      'America/Argentina/Buenos_Aires': 'Buenos Aires (GMT-3)',
      'Europe/Madrid': 'Madrid (GMT+1)',
    };
  }

  /**
   * Obtiene las especialidades médicas disponibles desde el catálogo de BD.
   * Si el endpoint falla, cae a una lista mínima estática para que el formulario
   * no quede vacío.
   */
  async getEspecialidadesDisponibles(): Promise<string[]> {
    try {
      const { especialidadPerfilService } = await import('./especialidadPerfilService');
      const data = await especialidadPerfilService.listarActivas();
      return data.map((e) => e.nombre);
    } catch (e) {
      console.warn('No se pudo cargar el catálogo de especialidades, usando fallback', e);
      return [
        'Medicina Interna', 'Cirugía General', 'Pediatría',
        'Ginecología y Obstetricia', 'Medicina de Urgencias'
      ];
    }
  }
}

export const perfilUsuarioService = new PerfilUsuarioService();